import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppError } from '../../api/errors.js';
import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type {
  AnnotationSet,
  CreateAnnotationSetRequest,
} from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';
import { withCorrelationId } from '../../services/usageContext.js';
import { useScenarioFactory } from '../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../telemetry/scenarioError.js';
import { ScenarioName } from '../../telemetry/scenarios.js';

const ACCESS_DENIED_MESSAGE =
  'Access denied. You do not have permission to view annotation sets for this dataset.';

export interface UseAnnotationSetsResult {
  annotationSets: AnnotationSet[];
  totalCount: number | null;
  isLoading: boolean;
  error: string | null;
  /** HTTP status code when the error originated from an API response. */
  errorStatusCode: number | undefined;
  refresh: () => void;
  createAnnotationSet: (
    request: CreateAnnotationSetRequest,
  ) => Promise<AnnotationSet>;
}

export function useAnnotationSets(dataSetId: string): UseAnnotationSetsResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const scenarioFactory = useScenarioFactory();

  const [annotationSets, setAnnotationSets] = useState<AnnotationSet[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<number | undefined>(
    undefined,
  );
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    setErrorStatusCode(undefined);

    service
      .listAnnotationSets({ dataSetId, includeCount: true })
      .then((result) => {
        if (cancelled) return;
        setAnnotationSets(result.items);
        setTotalCount(result.totalCount);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const statusCode = err instanceof AppError ? err.statusCode : undefined;
        const message =
          statusCode === 403
            ? ACCESS_DENIED_MESSAGE
            : err instanceof Error
              ? err.message
              : 'Failed to load annotation sets';
        setError(message);
        setErrorStatusCode(statusCode);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [service, dataSetId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const createAnnotationSet = useCallback(
    async (request: CreateAnnotationSetRequest) => {
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeCreateAnnotationSet,
      );
      try {
        const result = await service.createAnnotationSet(dataSetId, request, {
          extras: withCorrelationId(scenario.queryId),
        });
        scenario.complete('success');
        refresh();
        return result;
      } catch (error) {
        const classification = classifyScenarioError(error);
        scenario.complete(classification.status, classification.data);
        throw error;
      }
    },
    [service, dataSetId, refresh, scenarioFactory],
  );

  return {
    annotationSets,
    totalCount,
    isLoading,
    error,
    errorStatusCode,
    refresh,
    createAnnotationSet,
  };
}
